import { isOrganizerHost, PUBLIC_SITE_ORIGIN } from '../../config/host';
import styles from './EventLink.module.css';

interface EventLinkProps {
  /** Event id — the link targets `/event/{id}` so crawlers can follow it. */
  eventId: string;
  /** The card's existing click action: its callback, or the navigate with backgroundLocation. */
  onClick?: () => void;
  /**
   * Extends the hit area over the whole card through the link's ::after
   * (the card must be position:relative — every event card is). The link
   * sits on the title while the whole card is clickable today; a wrapping
   * <a> would nest interactive elements (the remove button, the old
   * EventFeatCard <button>) invalidly.
   */
  extended?: boolean;
  /**
   * Tab order control. Non-top cards of a swipe stack stay out of the tab
   * order (tabIndex -1), exactly like today.
   */
  tabIndex?: number;
  children: React.ReactNode;
}

/**
 * Crawlable event link (R17): a real `<a href>` on the card title that
 * keeps the card's current click behaviour.
 *
 * - Plain left click → never a full page load: with an action, preventDefault
 *   + the caller's action and no bubbling (so a container onClick can't run
 *   the same action twice); without an action the click bubbles, so an
 *   ancestor container that owns the card (the organizer dashboard wrapper)
 *   still runs its own.
 * - Ctrl/Cmd/Shift/Alt click or middle click → NOT intercepted: the browser
 *   follows the href natively (new tab) and containers never see it.
 * - On the organizer host (no /event route) the href is the absolute public
 *   event URL; everywhere else it stays the relative /event/{id}.
 */
export function EventLink({ eventId, onClick, extended, tabIndex, children }: EventLinkProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Modified/middle clicks: no interception (native new tab), and kept
    // away from container onClicks so the same click can't fire twice.
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
      e.stopPropagation();
      return;
    }
    // A plain click never triggers a full page load.
    e.preventDefault();
    // No action of its own: bubble so an ancestor container may own it.
    if (!onClick) return;
    e.stopPropagation();
    onClick();
  };

  const href = isOrganizerHost()
    ? `${PUBLIC_SITE_ORIGIN}/event/${eventId}`
    : `/event/${eventId}`;

  return (
    <a
      href={href}
      onClick={handleClick}
      tabIndex={tabIndex}
      className={extended ? `${styles.link} ${styles.extended}` : styles.link}
    >
      {children}
    </a>
  );
}
